<script setup>
import { usePage } from '@inertiajs/vue3'
import NavLink from '@/Components/NavLink.vue'
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import { ref, onMounted, watch } from 'vue'

const isScrollingUp = ref(true)

onMounted(() => {
    document.addEventListener('scroll', handleScroll)
})

const handleScroll = () => {
    const currentScroll = window.scrollY || document.documentElement.scrollTop
    if (currentScroll > handleScroll.prevScroll) {
        if (currentScroll > 100) {
            isScrollingUp.value = false
        }
    } else {
        isScrollingUp.value = true
    }

    handleScroll.prevScroll = currentScroll
}

const isOpen = ref(false)

watch(
    () => isOpen.value,
    () => {
        if (isOpen.value) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = null
        }
    }
)

const page = usePage().props

const showWebSettingNews = ref(true)

const closeWebSettingNews = () => {
    showWebSettingNews.value = false
    sessionStorage.setItem('webSettingNewsClosed', 'true')
}

onMounted(() => {
    const webSettingNewsClosed = sessionStorage.getItem('webSettingNewsClosed')

    if (webSettingNewsClosed === 'true') {
        showWebSettingNews.value = false
    }
})
</script>

<template>
    <nav
        class="faster-animation fixed left-0 top-0 z-50 w-full"
        :class="
            {
                true: 'visible bg-[#1D1D1B]',
                false: 'invisible opacity-0',
            }[isScrollingUp]
        "
    >
        <div
            v-if="showWebSettingNews && page['webSettingNews']?.value && page['webSettingNews']?.value['description']?.length > 0"
            :style="{
                backgroundColor: page['webSettingNews']?.value['bg_color'],
                color: page['webSettingNews']?.value['text_color'],
            }"
            class="flex flex-row items-center justify-center px-2 py-1 text-center xl:px-0"
        >
            <p v-html="page['webSettingNews']?.value['description']" />

            <div class="relative left-4 flex items-center justify-center rounded-full bg-red-500 p-1">
                <button
                    @click="closeWebSettingNews"
                    class="text-white"
                    aria-label="Close"
                >
                    <XMarkIcon class="size-5" />
                </button>
            </div>
        </div>

        <div class="section--width mx-auto hidden grid-cols-12 px-2 py-5 lg:grid">
            <div class="col-span-2 flex space-x-4">
                <Link
                    :href="route('home.index')"
                    aria-label="domů"
                >
                    <img
                        class="w-auto object-contain sm:h-28"
                        src="/src/logo/kam-zajit-olomouc-logo-white.png"
                        loading="eager"
                        alt="Kam zajít Olomouc"
                    />
                </Link>
            </div>

            <div class="col-span-8">
                <div class="text-center font-caveat text-5xl font-semibold leading-10 text-white">
                    <Link
                        :href="route('home.index')"
                        aria-label="domů"
                    >
                        Kam zajít Olomouc
                    </Link>
                </div>

                <hr class="mx-auto my-5 w-10/12 border-transparent bg-white p-[0.5px]" />

                <div class="scroll-hidden w-full overflow-y-auto whitespace-nowrap text-center">
                    <NavLink
                        :active="route().current('home.*')"
                        :href="route('home.index')"
                        aria-label="domů"
                    >
                        Domů
                    </NavLink>

                    <NavLink
                        :active="route().current('businesses.*')"
                        :href="route('businesses.index')"
                        aria-label="Podniky"
                    >
                        Podniky
                    </NavLink>

                    <NavLink
                        :active="route().current('services.*')"
                        :href="route('services.index')"
                        aria-label="Služby"
                    >
                        Služby
                    </NavLink>

                    <NavLink
                        :active="route().current('blogs.*')"
                        :href="route('blogs.index')"
                        aria-label="Blogy"
                    >
                        Blogy
                    </NavLink>

                    <NavLink
                        :active="route().current('about.*')"
                        :href="route('about.index')"
                        aria-label="O nás"
                    >
                        O nás
                    </NavLink>
                </div>
            </div>

            <div class="col-span-2 flex items-center justify-end space-x-4">
                <div class="faster-animation flex rounded-full bg-white hover:bg-gray-200">
                    <a
                        class="lg:p-1 xl:p-2"
                        href="https://www.instagram.com/kamzajitolomouc"
                        aria-label="Instagram - Kam zajít Olomouc"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            class="size-5 object-cover xl:size-6"
                            src="/src/socials/instagram.webp"
                            alt="Instagram logo - Kam zajít Olomouc"
                        />
                    </a>
                </div>

                <div class="faster-animation flex rounded-full bg-white hover:bg-gray-200">
                    <a
                        class="lg:p-1 xl:p-2"
                        href="https://www.facebook.com/kamzajitolomouc"
                        aria-label="Facebook - Kam zajít Olomouc"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            class="size-5 object-cover xl:size-6"
                            src="/src/socials/facebook.webp"
                            alt="Facebook logo - Kam zajít Olomouc"
                        />
                    </a>
                </div>

                <div class="faster-animation flex rounded-full bg-white hover:bg-gray-200">
                    <a
                        class="lg:p-1 xl:p-2"
                        href="https://www.youtube.com/@kamzajitolomouc"
                        aria-label="Youtube - Kam zajít Olomouc"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            class="size-5 object-cover xl:size-6"
                            src="/src/socials/youtube.webp"
                            alt="Youtube logo - Kam zajít Olomouc"
                        />
                    </a>
                </div>

                <div class="faster-animation flex rounded-full bg-white hover:bg-gray-200">
                    <a
                        class="lg:p-1 xl:p-2"
                        href="https://www.tiktok.com/@kamzajitolomouc_?lang=en"
                        aria-label="TikTok - Kam zajít Olomouc"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            class="size-5 object-cover xl:size-6"
                            src="/src/socials/tiktok.webp"
                            alt="TikTok logo - Kam zajít Olomouc"
                        />
                    </a>
                </div>
            </div>
        </div>

        <div class="relative flex items-center border-b lg:hidden">
            <div class="relative flex w-full items-center justify-between p-2 pl-4">
                <div>
                    <Link
                        :href="route('home.index')"
                        aria-label="domů"
                    >
                        <img
                            class="h-12 w-auto object-contain"
                            src="/src/logo/kam-zajit-olomouc-logo-white.png"
                            alt="Kam zajít Olomouc"
                        />
                    </Link>
                </div>

                <button
                    @click="isOpen = !isOpen"
                    class="relative rounded border border-gray-300 bg-white p-1"
                    aria-label="mobile-menu"
                >
                    <component
                        :is="isOpen ? XMarkIcon : Bars3Icon"
                        class="h-8 w-8 text-gray-500"
                    />
                </button>
            </div>

            <div
                :class="
                    {
                        true: 'translate-x-0 opacity-100',
                        false: 'translate-x-full opacity-0',
                    }[isOpen]
                "
                class="absolute top-full z-20 h-screen w-full border-t border-gray-300 bg-gray-100 px-4 transition-all duration-300 ease-in-out"
            >
                <ul class="flex flex-col">
                    <li class="py-5">
                        <div class="text-center">
                            <Link
                                class="font-caveat text-5xl font-semibold leading-10 text-gray-800"
                                aria-label="domů"
                                :href="route('home.index')"
                            >
                                Kam zajít Olomouc
                            </Link>
                        </div>
                    </li>

                    <li class="py-2">
                        <Link
                            :href="route('home.index')"
                            class="flex items-center space-x-4 px-2 py-1"
                            aria-label="domů"
                            @click="isOpen = false"
                        >
                            <span class="font-base text-2xl text-gray-700"> Domů </span>
                        </Link>
                    </li>

                    <li class="py-2">
                        <Link
                            :href="route('businesses.index')"
                            class="flex items-center space-x-4 px-2 py-1"
                            aria-label="Podniky"
                            @click="isOpen = false"
                        >
                            <span class="font-base text-2xl text-gray-700"> Podniky </span>
                        </Link>
                    </li>

                    <li class="py-2">
                        <Link
                            :href="route('services.index')"
                            class="flex items-center space-x-4 px-2 py-1"
                            aria-label="Služby"
                            @click="isOpen = false"
                        >
                            <span class="font-base text-2xl text-gray-700"> Služby </span>
                        </Link>
                    </li>

                    <li class="py-2">
                        <Link
                            :href="route('blogs.index')"
                            class="flex items-center space-x-4 px-2 py-1"
                            aria-label="Blogy"
                            @click="isOpen = false"
                        >
                            <span class="font-base text-2xl text-gray-700"> Blogy </span>
                        </Link>
                    </li>

                    <li class="py-2">
                        <Link
                            :href="route('about.index')"
                            class="flex items-center space-x-4 px-2 py-1"
                            aria-label="O nás"
                            @click="isOpen = false"
                        >
                            <span class="font-base text-2xl text-red-400"> O nás </span>
                        </Link>
                    </li>
                </ul>
            </div>
        </div>
    </nav>
</template>
